import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
} from '@mui/material';
import {
  Dashboard,
  People,
  Engineering,
  Inventory,
  ShoppingCart,
  Assessment,
  Build,
  Category,
  LocalShipping,
  Verified,
  ProductionQuantityLimits,
  ExpandLess,
  ExpandMore,
  SupervisorAccount,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRbac } from '../../context/RbacContext';
import { useAuth } from '../../context/AuthContext';
import { SimpleFooter } from '../layout/SimpleFooter';
import { Topbar } from '../layout/Topbar';
import { getRoleName } from '../../utils/roleUtils';
import { BRAND } from '../../config/brand';
import {
  NAVY,
  NAVY_DARK,
  getNavItemSx,
  getNavIconSx,
  getNavTextProps,
  getNavChildrenListSx,
} from '../../utils/portalNavStyles';

interface ManagerLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 280;

const managerMenuItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/manager/dashboard',
    icon: <Dashboard />,
    permission: 'manager:dashboard',
  },
  {
    id: 'production-management',
    label: 'Production Operations',
    icon: <Engineering />,
    permission: 'manager:production',
    children: [
      {
        id: 'production-hub',
        label: 'Operations Hub',
        path: '/manager/production/operations',
        icon: <Engineering />,
        permission: 'manager:production',
      },
      {
        id: 'production-lines',
        label: 'Production Lines',
        path: '/manager/production/lines',
        icon: <ProductionQuantityLimits />,
        permission: 'manager:production',
      },
      {
        id: 'production-queue',
        label: 'Production Queue',
        path: '/manager/production/queue',
        icon: <ProductionQuantityLimits />,
        permission: 'manager:production',
      },
      {
        id: 'machine-floor',
        label: 'Machine Floor',
        path: '/manager/production/machines',
        icon: <Build />,
        permission: 'manager:production',
      },
      {
        id: 'warehouse',
        label: 'Warehouse',
        path: '/manager/warehouse',
        icon: <Inventory />,
        permission: 'manager:inventory',
      },
      {
        id: 'deliveries',
        label: 'Deliveries',
        path: '/manager/deliveries',
        icon: <LocalShipping />,
        permission: 'manager:orders',
      },
      {
        id: 'financials',
        label: 'Financials',
        path: '/manager/financials',
        icon: <Assessment />,
        permission: 'manager:orders',
      },
    ],
  },
  {
    id: 'inventory-management',
    label: 'Inventory Management',
    path: '/manager/inventory',
    icon: <Inventory />,
    permission: 'manager:inventory',
  },
  {
    id: 'quality-control',
    label: 'Quality Control',
    path: '/manager/quality',
    icon: <Verified />,
    permission: 'manager:quality',
  },
  {
    id: 'maintenance-management',
    label: 'Maintenance',
    path: '/manager/maintenance',
    icon: <Build />,
    permission: 'manager:maintenance',
  },
  {
    id: 'order-management',
    label: 'Order Management',
    path: '/manager/orders',
    icon: <ShoppingCart />,
    permission: 'manager:orders',
  },
  {
    id: 'customer-management',
    label: 'Customer Management',
    path: '/manager/customers',
    icon: <People />,
    permission: 'manager:customers',
  },
  {
    id: 'product-management',
    label: 'Product Management',
    icon: <Category />,
    permission: 'manager:products',
    children: [
      {
        id: 'products-list',
        label: 'Products List',
        path: '/manager/products',
        icon: <Category />,
        permission: 'manager:products',
      },
      {
        id: 'product-configurator',
        label: 'Product Configurator',
        path: '/manager/products/configurator/:productId',
        icon: <Category />,
        permission: 'manager:products',
      },
      {
        id: 'product-display',
        label: 'Product Display',
        path: '/manager/products/display/:productId',
        icon: <Category />,
        permission: 'manager:products',
      },
    ],
  },
  {
    id: 'supply-chain',
    label: 'Supply Chain',
    path: '/manager/supply-chain',
    icon: <LocalShipping />,
    permission: 'manager:supply-chain',
  },
  {
    id: 'reports',
    label: 'Reports & Analytics',
    path: '/manager/reports',
    icon: <Assessment />,
    permission: 'manager:reports',
  },
];

export const ManagerLayout: React.FC<ManagerLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, user, loading } = useRbac();
  const isManager = hasRole('manager');
  const [expanded, setExpanded] = useState<string[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const activeParents = managerMenuItems
      .filter((item) => item.children?.some((child) => child.path && location.pathname.startsWith(child.path.split(':')[0])))
      .map((item) => item.id);
    if (activeParents.length > 0) {
      setExpanded((prev) => Array.from(new Set([...prev, ...activeParents])));
    }
  }, [location.pathname]);

  const handleExpandClick = (id: string) => {
    setExpanded(prev =>
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const renderMenuItem = (item: any, level: number = 0) => {
    if (!isManager) {
      return null;
    }
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expanded.includes(item.id);
    const isActive = item.path ? location.pathname === item.path || location.pathname.startsWith(`${item.path.split(':')[0]}`) : false;
    if (hasChildren) {
      return (
        <Box key={item.id}>
          <ListItemButton onClick={() => handleExpandClick(item.id)} sx={getNavItemSx(level, isExpanded)}>
            <ListItemIcon sx={getNavIconSx(level)}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={getNavTextProps(level)} />
            {isExpanded ? <ExpandLess sx={{ color: NAVY }} /> : <ExpandMore sx={{ color: NAVY }} />}
          </ListItemButton>
          <Collapse in={isExpanded} timeout="auto">
            <List component="div" disablePadding sx={getNavChildrenListSx()}>
              {item.children.map((child: any) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        </Box>
      );
    }
    return (
      <ListItem key={item.id} disablePadding>
        <ListItemButton onClick={() => handleNavigation(item.path)} sx={getNavItemSx(level, isActive)} selected={isActive}>
          <ListItemIcon sx={getNavIconSx(level)}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} primaryTypographyProps={getNavTextProps(level)} />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand Section */}
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <SupervisorAccount sx={{ fontSize: 40, mb: 1 }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #ffffff 0%, #d1fae5 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Manager Portal
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Production Management System
        </Typography>
      </Box>
      {/* User Info */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ mr: 2, bgcolor: NAVY }}>
            {user?.name?.[0] || 'M'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getRoleName(user?.role as any)} • {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Menu Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 2 }}>
        <List>
          {managerMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>
      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Manager Portal v1.0
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © 2024 Monio Production
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: theme.palette.background.paper,
              zIndex: theme.zIndex.drawer,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: theme.palette.background.paper,
            borderRight: 1,
            borderColor: 'divider',
            zIndex: theme.zIndex.drawer,
            position: 'fixed',
            height: '100vh',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Top Bar */}
        <Topbar onDrawerToggle={() => setMobileOpen(true)} notificationsCount={4} />
        {/* Page Content */}
        <Box sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          mt: '64px',
        }}>
          <Box sx={{ 
            flexGrow: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ flexGrow: 1 }}>
              {children}
            </Box>
            <SimpleFooter />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}; 